import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $findMatchingParent } from '@lexical/utils';
import {
  $getSelection,
  $isElementNode,
  $isRangeSelection,
  $isTextNode,
  COMMAND_PRIORITY_EDITOR,
  createCommand,
  ElementNode,
  LexicalCommand,
  LexicalNode,
} from 'lexical';
import { useEffect } from 'react';

import { isSelectionInTable } from '../ToolbarPlugin/tableUtils';
import {
  $createFontSizeNode,
  $isFontSizeNode,
  FontSizeNode,
} from './FontSizeNode';

/**
 * Command payload: a font size number to apply, or null to remove font sizing.
 */
export const SET_FONT_SIZE_COMMAND: LexicalCommand<number | null> =
  createCommand('SET_FONT_SIZE_COMMAND');

/**
 * Check whether a node or any of its descendants has a key in the given set.
 * selection.extract() returns leaf text nodes, so when an intermediate inline
 * element (e.g. HighlightNode, LinkNode) wraps selected text, only the leaf
 * keys appear in selectedKeys — not the wrapper's key.
 */
function $hasSelectedDescendant(
  node: LexicalNode,
  selectedKeys: Set<string>
): boolean {
  if (selectedKeys.has(node.getKey())) return true;
  if ($isElementNode(node)) {
    for (const child of node.getChildren()) {
      if ($hasSelectedDescendant(child, selectedKeys)) return true;
    }
  }
  return false;
}

/**
 * After a partially-selected intermediate inline element (e.g. HighlightNode)
 * is moved out of a FontSizeNode, re-wrap its unselected children at the
 * original size so they don't lose their font sizing.
 *
 * Example: `HighlightNode["AAA ", "BBB"]` with "BBB" selected becomes
 * `HighlightNode[FontSizeNode(orig, "AAA "), "BBB"]`.
 */
function $reWrapUnselectedChildren(
  element: ElementNode,
  originalSize: number,
  selectedKeys: Set<string>
): void {
  for (const child of element.getChildren()) {
    // Fully selected leaf — leave for Step 3 to re-wrap at the new size
    if (selectedKeys.has(child.getKey())) continue;

    if (!$hasSelectedDescendant(child, selectedKeys)) {
      // Fully unselected — wrap at original size
      const wrapper = $createFontSizeNode(originalSize);
      child.insertBefore(wrapper);
      wrapper.append(child);
    } else if ($isElementNode(child)) {
      // Partially selected nested element — recurse
      $reWrapUnselectedChildren(child, originalSize, selectedKeys);
    }
  }
}

/**
 * Split a FontSizeNode around selected children. Unselected children stay
 * wrapped at the original size; selected children are moved out so they can
 * be re-wrapped at a new size (or left unwrapped for removal).
 *
 * When an intermediate inline element (e.g. HighlightNode) is partially
 * selected, it is moved out and its unselected children are re-wrapped at
 * the original size so they don't lose their font sizing.
 *
 * Example: `[size=16]AAA BBB CCC[/size]` with "BBB" selected becomes
 * `[size=16]AAA [/size]` + `BBB` (unwrapped) + `[size=16] CCC[/size]`.
 */
function $splitFontSizeNode(
  fontSizeNode: FontSizeNode,
  selectedKeys: Set<string>
): void {
  const originalSize = fontSizeNode.getFontSize();
  const children = fontSizeNode.getChildren();

  // Group children into contiguous runs of selected / not-selected.
  // We check descendants because selection.extract() returns leaf nodes,
  // which may be nested inside intermediate inline elements (HighlightNode, etc.).
  const groups: { nodes: LexicalNode[]; selected: boolean }[] = [];
  for (const child of children) {
    const isSelected = $hasSelectedDescendant(child, selectedKeys);
    const lastGroup = groups[groups.length - 1];
    if (lastGroup && lastGroup.selected === isSelected) {
      lastGroup.nodes.push(child);
    } else {
      groups.push({ nodes: [child], selected: isSelected });
    }
  }

  // Replace the original FontSizeNode with groups
  for (const group of groups) {
    if (group.selected) {
      // Move selected nodes out — they'll be re-wrapped by the caller
      for (const node of group.nodes) {
        fontSizeNode.insertBefore(node);
        // If this is a partially-selected intermediate inline element,
        // re-wrap its unselected children at the original size so they
        // don't lose their font sizing.
        if ($isElementNode(node) && node.isInline()) {
          $reWrapUnselectedChildren(node, originalSize, selectedKeys);
        }
      }
    } else {
      // Keep unselected nodes wrapped at the original size
      const wrapper = $createFontSizeNode(originalSize);
      fontSizeNode.insertBefore(wrapper);
      for (const node of group.nodes) {
        wrapper.append(node);
      }
    }
  }

  fontSizeNode.remove();
}

/**
 * Apply a font size to the current range selection. Pass `null` to unwrap
 * (remove any FontSizeNode wrappers around the selection).
 *
 * Must be called inside an editor.update() block — exported separately from
 * the React plugin so it can be unit-tested without mounting React.
 */
export function $applyFontSizeToSelection(fontSize: number | null): boolean {
  const selection = $getSelection();
  if (!$isRangeSelection(selection)) return false;

  // Custom font sizes are not supported inside table cells
  if (isSelectionInTable()) return false;

  const nodes = selection.extract();
  const selectedKeys = new Set(nodes.map((n) => n.getKey()));

  // Step 1: Split any FontSizeNodes that contain selected nodes.
  // Unselected siblings keep the original size; selected nodes are freed.
  const processed = new Set<string>();
  for (const node of nodes) {
    const fontSizeParent = $findMatchingParent(node, $isFontSizeNode);
    if (fontSizeParent && !processed.has(fontSizeParent.getKey())) {
      processed.add(fontSizeParent.getKey());
      $splitFontSizeNode(fontSizeParent, selectedKeys);
    }
  }

  // null means "unwrap". The dropdown is responsible for sending null
  // when the user picks the editor's base size, since the base size
  // varies by editor (16pt in slides, 12pt in diagram text notes).
  if (fontSize === null) return true;

  // Step 2: Re-read the selection after splitting — node references
  // from the original extract may be stale.
  const freshSelection = $getSelection();
  if (!$isRangeSelection(freshSelection)) return true;
  const freshNodes = freshSelection.extract();

  // Step 3: Wrap selected nodes in a new FontSizeNode.
  for (const node of freshNodes) {
    if (!node.isAttached()) continue;

    // Skip non-text, non-element nodes (e.g. linebreaks)
    if (!$isTextNode(node) && !$isElementNode(node)) continue;
    // Don't wrap block-level elements or existing FontSizeNodes
    if ($isElementNode(node) && !node.isInline()) continue;
    if ($isFontSizeNode(node)) continue;

    const fontSizeNode = $createFontSizeNode(fontSize);
    node.insertBefore(fontSizeNode);
    fontSizeNode.append(node);
  }

  return true;
}

/**
 * Collapse a FontSizeNode with an immediate next-sibling FontSizeNode of the
 * same size into a single node. Without this, operations like applying a font
 * size to a selection that spans previously-split text nodes (e.g. one half
 * bolded, the other plain) leave behind multiple adjacent same-size wrappers,
 * which on markdown export fragment as `[size=N]...[/size][size=N]...[/size]`
 * — breaking inline formatting that spans the boundary.
 *
 * Registered as a Lexical node transform so it runs after every update,
 * including after markdown import.
 *
 * A caret between the two nodes stays at the join. For example, Backspace
 * at the start of a sized paragraph merges it into the sized paragraph above
 * and puts the caret between the two wrappers, as an offset in the block.
 * When `next` is removed, that offset points to the end of the block instead.
 */
export function $mergeAdjacentFontSizeNodes(node: FontSizeNode): void {
  const next = node.getNextSibling();
  if (!$isFontSizeNode(next)) return;
  if (next.getFontSize() !== node.getFontSize()) return;

  const selection = $getSelection();
  if ($isRangeSelection(selection)) {
    const parentKey = node.getParentOrThrow().getKey();
    const joinIndex = next.getIndexWithinParent();
    // Prefer a text point: Lexical keeps text points correct when it later
    // joins the adjacent text nodes, but not element offsets. Use only a
    // direct child of `node`. A text node inside a nested wrapper, such as a
    // highlight, would put the caret inside that style.
    const lastChild = node.getLastChild();
    for (const point of [selection.anchor, selection.focus]) {
      if (
        point.type !== 'element' ||
        point.key !== parentKey ||
        point.offset !== joinIndex
      ) {
        continue;
      }
      if ($isTextNode(lastChild)) {
        point.set(lastChild.getKey(), lastChild.getTextContentSize(), 'text');
      } else {
        point.set(node.getKey(), node.getChildrenSize(), 'element');
      }
    }
  }

  for (const child of next.getChildren()) {
    node.append(child);
  }
  next.remove();
}

export function FontSizePlugin(): null {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    return editor.registerCommand(
      SET_FONT_SIZE_COMMAND,
      (fontSize) => $applyFontSizeToSelection(fontSize),
      COMMAND_PRIORITY_EDITOR
    );
  }, [editor]);

  useEffect(() => {
    return editor.registerNodeTransform(
      FontSizeNode,
      $mergeAdjacentFontSizeNodes
    );
  }, [editor]);

  return null;
}
